/**
 * Hulpen bij de kop van de klantkaart: naar welk adres Mail gaat en
 * welke nummers Bel en WhatsApp gebruiken.
 */
import { datumKort, dierenKop, isMobiel } from "../opmaak";
import type { KaartDier, KaartLightspeedAccount, KlantKaart } from "../types";

/**
 * Sessie 82 (tijdelijk tot ronde 80): geen lege blokken. Heeft een dier meer
 * dan een naam (ras, geslacht, leeftijd, een feit, vermijden of een foto)?
 * Anders is het blok Dier "onbekend" en blijft het weg, zonder vervangtekst.
 */
export function dierHeeftInhoud(dier: KaartDier): boolean {
  return Boolean(
    dier.ras ||
      dier.geslacht ||
      dier.castratie !== null ||
      dier.geboortedatum ||
      dier.leeftijd_maanden !== null ||
      dier.groeifase ||
      dier.rasgrootte ||
      dier.foto_url ||
      dier.vermijden.length > 0 ||
      dier.feiten.some((f) => f.waarde !== null),
  );
}

/** Zelfde regel voor Voeding: een cijfer of een bestelde soort, anders weg. */
export function voedingHeeftInhoud(dier: KaartDier): boolean {
  const v = dier.voeding;
  return Boolean(
    !dier.is_overleden &&
      (v.nodig || v.werkelijk || v.conclusie || dier.eet.some((e) => e.product_naam)),
  );
}

/** Het blok Dier: alleen als minstens één dier inhoud heeft. */
export function dierBlokTonen(kaart: KlantKaart): boolean {
  return kaart.dieren.some(dierHeeftInhoud);
}

export function voedingBlokTonen(kaart: KlantKaart): boolean {
  return kaart.dieren.some(voedingHeeftInhoud);
}

/** Herbestellen: alleen met een meter (backend laat onzinnige meters weg). */
export function herbestelBlokTonen(kaart: KlantKaart): boolean {
  return kaart.herbestellen.meters.length > 0;
}

/**
 * Kop van het blok Dier: "1 hond, geen andere dieren (gevraagd 2 jun)"
 * als het dossier weet of er andere dieren zijn, anders "1 hond".
 */
export function dierBlokTitel(kaart: KlantKaart): string {
  const kop = kaart.dieren.length > 0 ? dierenKop(kaart.dieren.map((d) => d.diersoort)) : "Dier";
  const andere = kaart.dossier?.andere_dieren;
  return andere ? `${kop}, ${andere}` : kop;
}

/** Het adres waar Mail heen gaat: het hoofdadres, anders het eerste. */
export function hoofdEmail(kaart: KlantKaart): string | null {
  const lijst = kaart.kop.emails;
  return (lijst.find((e) => e.herkomst === "hoofd") ?? lijst[0])?.adres ?? null;
}

/** Het nummer om te bellen en het mobiele nummer voor WhatsApp. */
export function nummers(kaart: KlantKaart): { bel: string | null; mobiel: string | null } {
  const lijst = kaart.kop.telefoons;
  const mobiel =
    lijst.find((t) => t.whatsapp)?.nummer ?? lijst.find((t) => isMobiel(t.nummer))?.nummer ?? null;
  return { bel: lijst[0]?.nummer ?? null, mobiel };
}

/** "chat:<id>" of "whatsapp:<id>" als woord; een leesbare bron blijft staan. */
export function fotoBronWoord(bron: string | null): string | null {
  if (!bron) return null;
  const soort = bron.includes(":") ? bron.split(":")[0].toLowerCase() : null;
  if (soort === null) return bron;
  const woord: Record<string, string> = { chat: "uit chat", whatsapp: "uit WhatsApp", mail: "uit mail" };
  return woord[soort] ?? soort;
}

/** Naam van een account: "Lightspeed 123", anders e-mailadres en woonplaats. */
export function accountNaam(a: KaartLightspeedAccount): string {
  if (a.lightspeed_id) return `Lightspeed ${a.lightspeed_id}`;
  const delen = [a.email, a.woonplaats].filter(Boolean);
  return delen.length > 0 ? delen.join(", ") : "account zonder Lightspeed";
}

/**
 * "12 bestellingen, laatste 23 sep". Onbekend blijft onbekend; nul staat
 * er alleen als de server echt 0 stuurt.
 */
export function accountTelling(a: KaartLightspeedAccount): string {
  const n = a.aantal_bestellingen;
  const aantal =
    typeof n === "number"
      ? `${n} ${n === 1 ? "bestelling" : "bestellingen"}`
      : "bestellingen onbekend";
  if (n === 0) return aantal;
  const laatste = a.laatste_besteld_op ? datumKort(a.laatste_besteld_op) : "";
  return `${aantal}, laatste ${laatste || "onbekend"}`;
}

/** Query-sleutel van de reviews bij het klantprofiel. */
export const reviewsSleutel = (klantId: string) =>
  ["werkplek", "klant", klantId, "reviews"] as const;

/** "4 van 5", omgerekend naar vijf; zonder score niets. */
export function scoreWoord(score: number | null, max: number): string | null {
  if (score == null || max <= 0) return null;
  const vijf = Math.round(((score * 5) / max) * 10) / 10;
  return `${String(vijf).replace(".", ",")} van 5`;
}
